import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Navigation, Plus, Phone, Mail, FileText, Building2, User } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can, canFinancial } from "@/lib/permissions";
import { fmtDate, fmtMoney } from "@/lib/format";
import { Loading, SectionCard, EmptyState, Field } from "@/components/common";
import { DataTable, Btn, TextInput } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Modal } from "@/components/Modal";

export default function ClientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [contactModal, setContactModal] = useState(false);
  const [contact, setContact] = useState({ first_name: "", last_name: "", job_title: "", email: "", telephone: "", contact_type: "Site", is_primary: false });

  const load = () => api.get(`/clients/${id}/detail`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [id]);
  if (!d) return <Loading />;
  const c = d.client;
  const fin = canFinancial(user);

  const saveContact = async () => {
    if (!contact.first_name) return toast.error("First name required");
    try { await api.post("/contacts", { ...contact, client_id: id }); toast.success("Contact added"); setContactModal(false); setContact({ first_name: "", last_name: "", job_title: "", email: "", telephone: "", contact_type: "Site", is_primary: false }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <button onClick={() => nav("/clients")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft size={16} /> Back to clients</button>
      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 rounded-lg bg-seq-forest text-seq-green flex items-center justify-center"><Building2 size={24} /></div>
          <div>
            <h1 className="text-2xl font-display font-semibold text-slate-900">{c.client_name}</h1>
            <p className="text-sm text-slate-500">{c.company_name} · Account manager: {c.account_manager || "—"}</p>
          </div>
        </div>
        <StatusBadge status={c.status} />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="mb-4">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="sites" data-testid="tab-sites">Sites ({d.sites.length})</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts ({d.contacts.length})</TabsTrigger>
          <TabsTrigger value="contracts" data-testid="tab-contracts">Contracts ({d.contracts.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Job History ({d.visits.length})</TabsTrigger>
          <TabsTrigger value="actions" data-testid="tab-actions">Actions ({d.actions.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid md:grid-cols-2 gap-4">
            <SectionCard title="Contact details">
              <dl className="space-y-2 text-sm">
                <Row label="Main address" value={c.main_address} />
                <Row label="Billing address" value={c.billing_address} />
                <Row label="Telephone" value={c.telephone} />
                <Row label="Email" value={c.email} />
                <Row label="Created" value={`${fmtDate(c.created_at)} by ${c.created_by}`} />
              </dl>
            </SectionCard>
            <SectionCard title="Notes">
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{c.notes || "No notes recorded."}</p>
            </SectionCard>
          </div>
        </TabsContent>

        <TabsContent value="sites">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {d.sites.map((s) => (
              <div key={s.id} className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-start justify-between">
                  <div className="font-medium text-slate-800 flex items-center gap-2"><MapPin size={16} className="text-seq-green" /> {s.site_name}</div>
                  <StatusBadge status={s.status} />
                </div>
                <p className="text-sm text-slate-500 mt-2">{s.site_address}</p>
                <p className="text-xs text-slate-400 mt-1">{s.eircode} · {s.opening_hours}</p>
                <div className="flex gap-2 mt-3">
                  <a target="_blank" rel="noreferrer" href={`https://www.google.com/maps/search/?api=1&query=${s.gps_lat},${s.gps_lng}`} className="text-xs inline-flex items-center gap-1 text-seq-green font-medium"><Navigation size={13} /> Google Maps</a>
                  <a target="_blank" rel="noreferrer" href={`https://maps.apple.com/?ll=${s.gps_lat},${s.gps_lng}`} className="text-xs inline-flex items-center gap-1 text-slate-500 font-medium">Apple Maps</a>
                </div>
              </div>
            ))}
            {d.sites.length === 0 && <EmptyState icon={MapPin} title="No sites yet" />}
          </div>
        </TabsContent>

        <TabsContent value="contacts">
          <div className="flex justify-end mb-3">{can(user, "edit_clients") && <Btn onClick={() => setContactModal(true)} data-testid="add-contact-btn"><Plus size={15} /> Add contact</Btn>}</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {d.contacts.map((ct) => (
              <div key={ct.id} className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-center gap-2 font-medium text-slate-800"><User size={16} className="text-slate-400" /> {ct.first_name} {ct.last_name}
                  {ct.is_primary && <span className="text-[10px] bg-seq-green/15 text-seq-green px-1.5 rounded font-semibold">PRIMARY</span>}</div>
                <p className="text-xs text-slate-500 mt-1">{ct.job_title}</p>
                <div className="mt-2 space-y-1 text-xs text-slate-500">
                  {ct.email && <div className="flex items-center gap-1.5"><Mail size={12} /> {ct.email}</div>}
                  {ct.telephone && <div className="flex items-center gap-1.5"><Phone size={12} /> {ct.telephone}</div>}
                </div>
              </div>
            ))}
            {d.contacts.length === 0 && <EmptyState icon={User} title="No contacts" />}
          </div>
        </TabsContent>

        <TabsContent value="contracts">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <DataTable rows={d.contracts} columns={[
              { key: "contract_name", label: "Contract", className: "font-medium text-slate-800" },
              { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
              { key: "start_date", label: "Start", render: (r) => fmtDate(r.start_date) },
              { key: "end_date", label: "End", render: (r) => fmtDate(r.end_date) },
              ...(fin ? [{ key: "annual_contract_value", label: "Value", align: "right", render: (r) => fmtMoney(r.annual_contract_value) }] : []),
            ]} />
          </div>
        </TabsContent>

        <TabsContent value="history">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <DataTable rows={d.visits} columns={[
              { key: "visit_ref", label: "Ref", className: "text-xs text-slate-400" },
              { key: "job_title", label: "Job", className: "font-medium text-slate-700" },
              { key: "scheduled_date", label: "Date", render: (r) => fmtDate(r.scheduled_date) },
              { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
              ...(fin ? [{ key: "charge", label: "Charge", align: "right", render: (r) => fmtMoney(r.charge) }] : []),
            ]} />
          </div>
        </TabsContent>

        <TabsContent value="actions">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <DataTable rows={d.actions} columns={[
              { key: "title", label: "Action", className: "font-medium text-slate-700" },
              { key: "action_type", label: "Type" },
              { key: "due_date", label: "Due", render: (r) => fmtDate(r.due_date) },
              { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
            ]} />
          </div>
        </TabsContent>
      </Tabs>

      <Modal open={contactModal} onOpenChange={setContactModal} title="Add contact"
        footer={<><Btn variant="secondary" onClick={() => setContactModal(false)}>Cancel</Btn><Btn onClick={saveContact} data-testid="save-contact-btn">Save</Btn></>}>
        <div className="grid grid-cols-2 gap-4">
          <Field label="First name" required><TextInput value={contact.first_name} onChange={(e) => setContact({ ...contact, first_name: e.target.value })} /></Field>
          <Field label="Last name"><TextInput value={contact.last_name} onChange={(e) => setContact({ ...contact, last_name: e.target.value })} /></Field>
          <Field label="Job title" className="col-span-2"><TextInput value={contact.job_title} onChange={(e) => setContact({ ...contact, job_title: e.target.value })} /></Field>
          <Field label="Email"><TextInput value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} /></Field>
          <Field label="Telephone"><TextInput value={contact.telephone} onChange={(e) => setContact({ ...contact, telephone: e.target.value })} /></Field>
        </div>
      </Modal>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4"><dt className="text-slate-400">{label}</dt><dd className="text-slate-700 text-right">{value || "—"}</dd></div>
);
